import { PlusIcon } from "@heroicons/react/20/solid";
import { Certificate } from "pmt-types/src/models/Certificate.ts";
import { Enrollee } from "pmt-types/src/models/Enrollee.ts";
import { Project } from "pmt-types/src/models/Project.ts";
import { Source } from "pmt-types/src/models/Source.ts";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { useMutationDeleteCertificate } from "../../hooks/mutations/useMutationDeleteCertificate.ts";
import { useMutationDownloadCertificate } from "../../hooks/mutations/useMutationDownloadCertificate.ts";
import { QueryKeys } from "../../hooks/queries/queryKeys.ts";
import cx from "../../utils/cx.ts";
import dateStringComparer from "../../utils/dateStringComparer.ts";
import formatDateString from "../../utils/formatDateString.ts";
import { Maybe } from "../../utils/types/maybe.ts";
import ErrorLabel from "../form/errorLabel.tsx";
import DeleteConfirmationModal from "./deleteConfirmationModal.tsx";

type Props = {
  source: Source;
  project: Project;
  enrollee: Enrollee;
  certificates: Maybe<Certificate[]>;
  onAddCertificate: () => void;
};

export default function EnrolleeCertificateList({
  source,
  project,
  enrollee,
  certificates,
  onAddCertificate,
}: Props) {
  const queryClient = useQueryClient();

  const {
    mutate: generatePdfLink,
    isLoading: isPdfLinkLoading,
    error: linkError,
  } = useMutationDownloadCertificate();

  const {
    mutate: deleteCertificate,
    isLoading: isCertificateDeleting,
    error: deleteError,
  } = useMutationDeleteCertificate();

  const [loadingCertificate, setLoadingCertificate] =
    useState<Maybe<Certificate>>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const hasCertificates = certificates != null && certificates.length > 0;

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Certificates
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {hasCertificates
              ? "All the certificates tied to this enrollee."
              : "No certificates added yet."}
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
            onClick={onAddCertificate}
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Add certificate
          </button>
        </div>
      </div>
      {hasCertificates && (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Created Date
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Effective Date
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Expiration Date
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                      >
                        <span className="sr-only">Edit</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {certificates
                      .sort((a, b) =>
                        dateStringComparer(a.createdDate, b.createdDate),
                      )
                      .map((certificate, index) => (
                        <tr key={index}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {formatDateString(certificate.createdDate)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {certificate.effectiveDate}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {certificate.expirationDate}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex flex-row gap-4">
                              <a
                                onClick={() => {
                                  if (isPdfLinkLoading) {
                                    return false;
                                  }

                                  setLoadingCertificate(certificate);

                                  generatePdfLink(certificate, {
                                    onSuccess: (data) => {
                                      window.open(
                                        data.url,
                                        "_blank",
                                        "noreferrer",
                                      );
                                    },
                                    onError: (error) =>
                                      console.error(
                                        "Error generating pdf link",
                                        error,
                                      ),
                                  });
                                }}
                                className={cx(
                                  isPdfLinkLoading &&
                                    loadingCertificate?.id == certificate.id
                                    ? "text-slate-600 hover:text-slate-900"
                                    : "text-sky-500 hover:text-sky-700",
                                  "cursor-pointer",
                                )}
                              >
                                View<span className="sr-only">, {index}</span>
                              </a>
                              <a
                                onClick={() => {
                                  if (isCertificateDeleting) {
                                    return false;
                                  }

                                  setLoadingCertificate(certificate);
                                  setIsDeleteModalOpen(true);
                                }}
                                className={cx(
                                  isCertificateDeleting &&
                                    loadingCertificate?.id == certificate.id
                                    ? "text-slate-600 hover:text-slate-900"
                                    : "text-sky-500 hover:text-sky-900",
                                  "hover:cursor-pointer",
                                )}
                              >
                                Delete
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <DeleteConfirmationModal
            entityName="certificate"
            isOpen={isDeleteModalOpen}
            isLoading={isCertificateDeleting}
            onConfirm={() => {
              if (loadingCertificate == null) {
                setIsDeleteModalOpen(false);
                return;
              }

              deleteCertificate(
                {
                  sourceId: source.id,
                  projectId: project.id,
                  enrolleeId: enrollee.id,
                  certificateId: loadingCertificate.id,
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries([
                      QueryKeys.GetEnrolleeCertificates,
                      enrollee.id,
                    ]);
                    setLoadingCertificate(null);
                    setIsDeleteModalOpen(false);
                  },
                  onError: (error) =>
                    console.error("Error deleting certificate", error),
                },
              );
            }}
            onClose={() => setIsDeleteModalOpen(false)}
          />
          {linkError != null && <ErrorLabel message={linkError?.toString()} />}
          {deleteError != null && (
            <ErrorLabel message={deleteError?.toString()} />
          )}
        </div>
      )}
    </div>
  );
}
